// Reader-facing rendering of a "video" article section: an embedded player where the
// provider allows it (YouTube, Vimeo, a direct video file), otherwise a card that opens
// the link in a new tab. See `resolveVideoEmbed` for why not everything can be embedded.
//
// With a thumbnail the section always starts as a still with a play button on top. For
// embeddable videos the click swaps in the player - that also keeps the provider's
// iframe (and its cookies) out of the page until a reader actually asks for it.

import { useState } from 'react'
import { Play, ExternalLink } from 'lucide-react'
import { getChapterImageUrl, fallbackToOriginalOnError } from '@/shared/lib/imageUtils'
import { resolveVideoEmbed, videoLinkLabel, type SectionVideo } from '@/shared/lib/articleSections'

type ArticleVideoProps = {
  video: SectionVideo | null
  className?: string
}

function Caption({ text }: { text: string }) {
  return (
    <p className="mt-2 px-1 text-xs leading-relaxed text-foreground/50 dark:text-white/75">
      {text}
    </p>
  )
}

/** The play button that sits centred on the still. */
function PlayBadge() {
  return (
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm transition group-hover:bg-black/60">
        <Play className="ml-0.5 h-7 w-7 fill-white text-white" />
      </span>
    </span>
  )
}

/**
 * Etwas höher als das 16:9 des Players, aber flacher als das Quellformat der meisten
 * Fotos. `object-cover` beschneidet dafür oben und unten, statt Ränder zu lassen.
 */
function Thumbnail({ fileId, alt }: { fileId: string; alt: string }) {
  return (
    <img
      src={getChapterImageUrl(fileId)}
      alt={alt}
      className="aspect-[3/2] w-full rounded-2xl object-cover"
      onError={fallbackToOriginalOnError}
    />
  )
}

export function ArticleVideo({ video, className = '' }: ArticleVideoProps) {
  const [isPlaying, setIsPlaying] = useState(false)

  if (!video?.url) return null

  const embed = resolveVideoEmbed(video.url)
  const thumbnailId = video.thumbnail_file_id
  const label = video.caption || 'Video ansehen'

  // A thumbnail on an embeddable video: show the still until it is clicked.
  if (thumbnailId && embed.kind !== 'link' && !isPlaying) {
    return (
      <figure className={className}>
        <button
          type="button"
          onClick={() => setIsPlaying(true)}
          aria-label={label}
          // Neutraler Platzhalter statt Schwarz: solange (oder falls) das Standbild nicht
          // lädt, sieht man eine dezente Fläche und keinen schwarzen Block.
          className="not-prose group relative block w-full overflow-hidden rounded-2xl bg-foreground/5 dark:bg-white/10"
        >
          <Thumbnail fileId={thumbnailId} alt={video.caption || ''} />
          <PlayBadge />
        </button>
        {video.caption && <Caption text={video.caption} />}
      </figure>
    )
  }

  // A thumbnail on a link-only video: the still itself is the link.
  if (thumbnailId && embed.kind === 'link') {
    return (
      <figure className={className}>
        <a
          href={embed.src}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="not-prose group relative block w-full overflow-hidden rounded-2xl bg-foreground/5 no-underline dark:bg-white/10"
        >
          <Thumbnail fileId={thumbnailId} alt={video.caption || ''} />
          <PlayBadge />
          <span className="absolute bottom-0 left-0 right-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-8 text-xs text-white/80">
            <ExternalLink className="h-3 w-3 shrink-0" />
            {videoLinkLabel(embed.src)}
          </span>
        </a>
        {video.caption && <Caption text={video.caption} />}
      </figure>
    )
  }

  return (
    <figure className={className}>
      {embed.kind === 'iframe' && (
        <div
          className="relative w-full overflow-hidden rounded-2xl bg-black"
          style={{ paddingTop: '56.25%' }}
        >
          <iframe
            src={isPlaying ? `${embed.src}?autoplay=1` : embed.src}
            title={video.caption || 'Video'}
            className="absolute inset-0 h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      {embed.kind === 'file' && (
        <video
          src={embed.src}
          controls
          autoPlay={isPlaying}
          playsInline
          className="w-full rounded-2xl bg-black"
        />
      )}

      {embed.kind === 'link' && (
        <a
          href={embed.src}
          target="_blank"
          rel="noopener noreferrer"
          className="not-prose flex items-center gap-3 rounded-2xl border border-border bg-white px-4 py-4 no-underline transition hover:bg-accent/40 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Play className="h-4 w-4 fill-current" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-foreground dark:text-white">
              {label}
            </span>
            <span className="block truncate text-xs text-foreground/50 dark:text-white/75">
              {videoLinkLabel(embed.src)}
            </span>
          </span>
          <ExternalLink className="h-4 w-4 shrink-0 text-foreground/40 dark:text-white/70" />
        </a>
      )}

      {/* The link card already shows the caption as its own title. */}
      {embed.kind !== 'link' && video.caption && <Caption text={video.caption} />}
    </figure>
  )
}
